<!--
  Turn Times — ported from CGS2022 Scripts/TurnTimes/turnTimes.vue and restyled to the 2.0 layout
  (tabs, submission date/time, three headline stats, one table, collapsible secondary groups).

  WHAT CAME ACROSS
  - The same two endpoints (api/turntimes/getuwturntimes, api/turntimes/getclosingturntimes) and
    the same field names, so this reads exactly what the CGS page reads today. Nothing new
    server-side.
  - The business-day engine: weekends and the federal holiday list skip, a submission on a
    non-business day counts from the next business day, "hours" items are wall-clock hours from
    the submission time (which is how the CGS page computed them).

  WHAT CHANGED, ON PURPOSE
  - Purchase and refinance rows are merged ("Conventional — Purchase & refinance"). If the desk
    ever sets the two to different values the row shows a range ("1–2 business days") and the
    expected return uses the longer one, so nothing is hidden by the merge.
  - Fixed: CGS's holiday check used toISOString(), which is UTC — after ~8 PM ET it looked up
    TOMORROW's date, so the evening before a holiday was treated as the holiday. Uses the local
    calendar date here.
  - Not fixed, flagged: CGS bound "Jumbo Conditions" to the FHA/VA/USDA conditions field
    (conditionturnsfhausdadays). Kept as-is so the numbers match the CGS page; if the API has a
    dedicated jumbo-conditions field, change JUMBO_CONDITIONS_FIELD below.
  - Kendo Card / dialogs / notifications and ajaxManager dropped; this app's api service + MSAL.
-->
<template>
  <div class="calc-page tt-page">
    <PageHeaderPortal>
      <div class="calc-header">
        <div class="calc-header__icon">
          <i class="fa-light fa-stopwatch"></i>
        </div>
        <div>
          <h1 class="calc-header__title">Turn Times</h1>
          <p class="calc-header__sub">Current underwriting and closing turn times, with expected return dates</p>
        </div>
      </div>
    </PageHeaderPortal>

    <section class="page-card tt-card">
      <!-- Tabs -->
      <div class="tt-tabs" role="tablist">
        <button
          v-for="t in tabs"
          :key="t.key"
          type="button"
          role="tab"
          class="tt-tab"
          :class="{ 'is-active': tab === t.key }"
          :aria-selected="tab === t.key"
          @click="tab = t.key"
        >
          {{ t.label }}
        </button>
      </div>

      <!-- Submission inputs -->
      <div class="tt-inputs">
        <label class="tt-field">
          <span class="tt-field__label">Submission date</span>
          <input v-model="calcDate" type="date" class="tt-input" />
        </label>
        <label class="tt-field">
          <span class="tt-field__label">Submission time (ET)</span>
          <input v-model="calcTime" type="time" class="tt-input" />
        </label>
        <span v-if="current && current.lastmodifiedon" class="tt-updated">
          Last updated {{ formatLongDate(current.lastmodifiedon) }}
        </span>
      </div>

      <div v-if="loading" class="tt-state">Loading turn times…</div>
      <div v-else-if="error" class="tt-state tt-state--error">{{ error }}</div>
      <div v-else-if="!current" class="tt-state">No turn times have been published yet.</div>

      <template v-else>
        <!-- Headline stats -->
        <div class="tt-stats">
          <div v-for="s in view.stats" :key="s.label" class="tt-stat">
            <div class="tt-stat__label">{{ s.label }}</div>
            <div class="tt-stat__value">{{ s.value }}</div>
            <div class="tt-stat__sub">{{ s.sub }}</div>
          </div>
        </div>

        <!-- Main table -->
        <h2 class="tt-h2">{{ view.tableTitle }}</h2>
        <div class="tt-table-wrap">
          <table class="tt-table">
            <thead>
              <tr>
                <th>Loan / request type</th>
                <th>Turn time</th>
                <th>Expected return</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in view.rows" :key="r.label">
                <td>{{ r.label }}</td>
                <td>
                  <a v-if="r.guidelines && guidelinesUrl" :href="guidelinesUrl" target="_blank" rel="noopener" class="tt-link">See current guidelines</a>
                  <span v-else-if="r.guidelines" class="tt-muted">See current guidelines</span>
                  <template v-else>{{ r.turn }}</template>
                </td>
                <td>{{ r.ret }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="tt-footnote">{{ view.footnote }}</p>

        <!-- Collapsible groups -->
        <div v-for="g in view.groups" :key="g.key" class="tt-acc" :class="{ 'is-open': openGroups[g.key] }">
          <button type="button" class="tt-acc__head" :aria-expanded="!!openGroups[g.key]" @click="toggle(g.key)">
            <span class="tt-acc__title">{{ g.title }}</span>
            <span class="tt-acc__summary">{{ g.summary }}</span>
            <i class="fa-light fa-chevron-down tt-acc__chev"></i>
          </button>
          <div v-show="openGroups[g.key]" class="tt-acc__body">
            <table class="tt-table tt-table--flush">
              <tbody>
                <tr v-for="r in g.rows" :key="r.label">
                  <td>{{ r.label }}</td>
                  <td>{{ r.turn }}</td>
                  <td>{{ r.ret }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <p class="tt-note">
          <i class="fa-light fa-circle-info"></i>
          Estimates may change with volume. Submissions received after 11:59 PM count toward the next business day.
        </p>
      </template>
    </section>
  </div>
</template>

<script>
  import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
  import apiService from "@/api/api.service.js"
  import auth from "@/authConfig.js"

  // Where "See current guidelines" points (MSHDA / Rural Development). The CGS page showed the
  // words with no link; set a URL here and the rows become links. Left null = plain text.
  const GUIDELINES_URL = null

  // See the header note — CGS reads the FHA/VA/USDA conditions field for Jumbo too.
  const JUMBO_CONDITIONS_FIELD = "conditionturnsfhausdadays"

  // Federal holidays — copied from CGS2022; extend as needed.
  const FEDERAL_HOLIDAYS = [
    "2025-01-01", "2025-01-20", "2025-02-17", "2025-05-26", "2025-06-19", "2025-07-04",
    "2025-09-01", "2025-10-13", "2025-11-11", "2025-11-27", "2025-12-25",
    "2026-01-01", "2026-01-19", "2026-02-16", "2026-05-25", "2026-06-19", "2026-07-03",
    "2026-07-04", "2026-09-07", "2026-10-12", "2026-11-11", "2026-11-26", "2026-12-25"
  ]

  const pad = n => String(n).padStart(2, "0")
  // Local calendar date, not toISOString() (UTC) — see header note.
  const localYmd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  const isBusinessDay = d => d.getDay() !== 0 && d.getDay() !== 6 && !FEDERAL_HOLIDAYS.includes(localYmd(d))

  function nextBusinessDay(date) {
    const d = new Date(date)
    d.setDate(d.getDate() + 1)
    while (!isBusinessDay(d)) d.setDate(d.getDate() + 1)
    return d
  }

  function addBusinessDays(fromDate, days) {
    const d = new Date(fromDate)
    let added = 0
    while (added < days) {
      d.setDate(d.getDate() + 1)
      if (isBusinessDay(d)) added++
    }
    return d
  }

  const num = v => {
    const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""))
    return isNaN(n) ? null : n
  }

  const fmtShort = d => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(d)
  const fmtTime = d => new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).format(d)

  export default {
    name: "TurnTimesView",
    components: { PageHeaderPortal },

    data() {
      const now = new Date()
      return {
        tabs: [
          { key: "uw", label: "Underwriting" },
          { key: "cl", label: "Closing" }
        ],
        tab: "uw",
        calcDate: localYmd(now),
        calcTime: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
        uw: null,
        cl: null,
        loading: true,
        error: "",
        openGroups: {},
        guidelinesUrl: GUIDELINES_URL
      }
    },

    computed: {
      current() {
        return this.tab === "uw" ? this.uw : this.cl
      },

      // The submission moment, rolled to the next business day when it lands on a weekend /
      // holiday or after the 11:59 PM cutoff. `rolled` matters for hour-based items, which then
      // start at 8:00 AM on that next business day (as on the CGS page).
      start() {
        if (!this.calcDate || !this.calcTime) return null
        let s = new Date(`${this.calcDate}T${this.calcTime}:00`)
        if (isNaN(s)) return null
        const [h, m] = this.calcTime.split(":").map(Number)
        const afterCutoff = h === 23 && m >= 59
        let rolled = false
        if (afterCutoff || !isBusinessDay(s)) {
          s = nextBusinessDay(s)
          rolled = true
        }
        return { at: s, rolled }
      },

      view() {
        return this.tab === "uw" ? this.uwView() : this.clView()
      }
    },

    created() {
      this.load()
    },

    methods: {
      async load() {
        this.loading = true
        this.error = ""
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse?.accessToken
          const [uwRes, clRes] = await Promise.all([
            apiService.GetUwTurnTimes(token),
            apiService.GetClosingTurnTimes(token)
          ])
          // Both endpoints return a one-row list (the CGS page v-for'd over it).
          this.uw = this.firstRow(uwRes)
          this.cl = this.firstRow(clRes)
        } catch (e) {
          this.error = e?.response?.data?.message || e?.message || "Turn times could not be loaded."
        } finally {
          this.loading = false
        }
      },

      firstRow(res) {
        const d = res?.data?.data ?? res?.data
        return Array.isArray(d) ? d[0] || null : d || null
      },

      toggle(key) {
        this.openGroups = { ...this.openGroups, [key]: !this.openGroups[key] }
      },

      // ── formatting ────────────────────────────────────────────────────────
      unitLabel(n, unit) {
        return `${n} business ${unit}${n === 1 ? "" : "s"}`
      },

      // One or more day values → "1 business day" or "1–2 business days".
      daysText(...vals) {
        const ns = vals.map(num).filter(n => n !== null)
        if (!ns.length) return "—"
        const lo = Math.min(...ns)
        const hi = Math.max(...ns)
        return lo === hi ? this.unitLabel(lo, "day") : `${lo}–${hi} business days`
      },

      returnDate(...vals) {
        const ns = vals.map(num).filter(n => n !== null && n > 0)
        if (!ns.length || !this.start) return null
        return addBusinessDays(this.start.at, Math.max(...ns))
      },

      returnHours(val) {
        const n = num(val)
        if (!n || !this.start) return null
        const s = new Date(this.start.at)
        if (this.start.rolled) s.setHours(8, 0, 0, 0)
        return new Date(s.getTime() + n * 3600 * 1000)
      },

      dayRow(label, ...vals) {
        const d = this.returnDate(...vals)
        return { label, turn: this.daysText(...vals), ret: d ? fmtShort(d) : "—" }
      },

      hourRow(label, val, unit = "business hour") {
        const n = num(val)
        const d = this.returnHours(val)
        return {
          label,
          turn: n === null ? "—" : `${n} ${unit}${n === 1 ? "" : "s"}`,
          ret: d ? `${fmtShort(d)} at ${fmtTime(d)}` : "—"
        }
      },

      dayStat(label, ...vals) {
        const d = this.returnDate(...vals)
        return { label, value: this.daysText(...vals), sub: d ? `Expected ${fmtShort(d)} by 11:59 PM ET` : "" }
      },

      formatLongDate(v) {
        const d = new Date(v)
        if (isNaN(d)) return ""
        return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(d)
      },

      footnote() {
        const y = this.start ? this.start.at.getFullYear() : new Date().getFullYear()
        return `All dates ${y}. Unless shown otherwise, returns by 11:59 PM ET.`
      },

      // ── tab content ───────────────────────────────────────────────────────
      uwView() {
        const t = this.uw || {}
        const appraisal = this.hourRow("Appraisal review", t.appraisalreviewshours)
        const condRows = [
          this.dayRow("Conventional loans", t.conditionturnsconvendays),
          this.dayRow("FHA, VA, USDA", t.conditionturnsfhausdadays),
          this.dayRow("Jumbo", t[JUMBO_CONDITIONS_FIELD])
        ]
        const directRows = [
          this.dayRow("Initial submissions", t.intsubs),
          this.dayRow("Resubmissions", t.resubs),
          this.dayRow("TBD purchases", t.pcrscrubs),
          this.hourRow("Emails — returned within", t.emails, "hour")
        ]
        return {
          stats: [
            this.dayStat("New submissions", t.cnplrdays),
            this.dayStat("Conditions", t.conditionturnsconvendays),
            {
              label: "Appraisal review",
              value: appraisal.turn,
              sub: appraisal.ret !== "—" ? `Expected ${appraisal.ret} ET` : ""
            }
          ],
          tableTitle: "Underwriting turn times",
          rows: [
            this.dayRow("Conventional — Purchase & refinance", t.cnplrdays, t.cnrlrdays),
            this.dayRow("FHA — Purchase & refinance", t.fhanplrdays, t.fhanrlrdays),
            this.dayRow("VA", t.vanulrdays),
            this.dayRow("USDA", t.usdanulrdays),
            this.dayRow("Jumbo", t.jumbonuhrdays),
            this.dayRow("Bond / DPA", t.bonddpadays),
            { label: "MSHDA", guidelines: true, ret: "—" },
            { label: "Rural Development", guidelines: true, ret: "—" },
            appraisal
          ],
          footnote: this.footnote(),
          groups: [
            {
              key: "uw-cond",
              title: "Conditions",
              summary: this.daysText(t.conditionturnsconvendays, t.conditionturnsfhausdadays, t[JUMBO_CONDITIONS_FIELD]),
              rows: condRows
            },
            {
              key: "uw-direct",
              title: "Direct submissions",
              summary: this.daysText(t.intsubs, t.resubs, t.pcrscrubs),
              rows: directRows
            }
          ]
        }
      },

      clView() {
        const t = this.cl || {}
        const month = new Intl.DateTimeFormat("en-US", { month: "long" }).format(this.start ? this.start.at : new Date())
        return {
          stats: [
            this.dayStat("Initial CD (ICD)", t.icddays),
            this.dayStat("CD request by end of business", t.cdrequestedbyendofbusinessdays),
            this.dayStat("Purchase CD delivery", t.expectedcddeliverydatedays)
          ],
          tableTitle: "Closing disclosure schedule",
          rows: [
            this.dayRow("Initial Closing Disclosure (ICD)", t.icddays),
            this.dayRow("CD request by end of business", t.cdrequestedbyendofbusinessdays),
            this.dayRow("Refinance — expected CD delivery", t.cdrequestedbyendofbusinessrefidays),
            this.dayRow("Purchase — expected CD delivery", t.expectedcddeliverydatedays)
          ],
          footnote: this.footnote(),
          groups: [
            {
              key: "cl-refi",
              title: `Refinance deadlines — ${month}`,
              summary: this.daysText(t.fharefianceclosingdeadlinedays, t.otherrefiancesclosingdeadlinedays),
              rows: [
                this.dayRow("FHA refinance — closing deadline", t.fharefianceclosingdeadlinedays),
                this.dayRow("All other refinances — closing deadline", t.otherrefiancesclosingdeadlinedays)
              ]
            }
          ]
        }
      }
    }
  }
</script>

<style scoped>
  /* Tokens (navy, gold, ink, muted, line, type scale) come from assets/css/calculatorKit.css via
     .calc-page. Only this page's layout lives here. */
  .tt-card {
    padding: 0 24px 20px;
  }

  /* ── tabs ── */
  .tt-tabs {
    display: flex;
    gap: 8px;
    border-bottom: 1px solid var(--calc-line);
  }

  .tt-tab {
    appearance: none;
    background: none;
    border: 0;
    border-bottom: 3px solid transparent;
    margin-bottom: -1px;
    padding: 14px 18px 11px;
    font-size: var(--fs-md);
    font-weight: 500;
    color: var(--calc-muted);
    cursor: pointer;
  }

  .tt-tab:hover {
    color: var(--calc-ink);
  }

  .tt-tab.is-active {
    color: var(--calc-navy);
    font-weight: 700;
    border-bottom-color: var(--calc-gold-lt);
  }

  /* ── inputs ── */
  .tt-inputs {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 16px;
    padding: 16px 0;
    border-bottom: 1px solid var(--calc-line);
  }

  .tt-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 190px;
  }

  .tt-field__label {
    font-size: var(--fs-xs);
    color: var(--calc-ink);
  }

  .tt-input {
    height: var(--control-h);
    padding: 0 10px;
    border: 1px solid #d5d9e2;
    border-radius: 6px;
    font-size: var(--fs-sm);
    color: var(--calc-ink);
    background: #fff;
  }

  .tt-input:focus {
    outline: 2px solid var(--calc-blue);
    outline-offset: -1px;
  }

  .tt-updated {
    margin-left: auto;
    align-self: center;
    font-size: var(--fs-xs);
    color: var(--calc-muted);
  }

  /* ── stats ── */
  .tt-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 22px 0;
    border-bottom: 1px solid var(--calc-line);
  }

  .tt-stat {
    padding: 4px 36px;
  }

  .tt-stat + .tt-stat {
    border-left: 1px solid var(--calc-line);
  }

  .tt-stat__label {
    font-size: var(--fs-xs);
    font-weight: 500;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: var(--calc-ink);
  }

  .tt-stat__value {
    margin-top: 6px;
    font-size: var(--fs-3xl);
    font-weight: 700;
    color: var(--calc-navy);
    letter-spacing: -0.01em;
  }

  .tt-stat__sub {
    margin-top: 6px;
    font-size: var(--fs-sm);
    color: var(--calc-muted);
  }

  /* ── table ── */
  .tt-h2 {
    margin: 22px 0 10px;
    font-size: var(--fs-lg);
    font-weight: 700;
    color: var(--calc-navy);
  }

  .tt-table-wrap {
    border: 1px solid var(--calc-line);
    border-radius: 8px;
    overflow: hidden;
  }

  .tt-table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--fs-sm);
    color: var(--calc-ink);
  }

  .tt-table th {
    background: #eef1f6;
    font-weight: 500;
    color: var(--calc-muted);
    text-align: left;
  }

  .tt-table th,
  .tt-table td {
    padding: 8px 14px;
    text-align: left;
  }

  /* Columns: name gets the room, turn time and return share the rest — as in the mock. */
  .tt-table th:first-child,
  .tt-table td:first-child {
    width: 45%;
  }

  .tt-table th:nth-child(2),
  .tt-table td:nth-child(2) {
    width: 30%;
  }

  .tt-table tbody tr + tr td {
    border-top: 1px solid var(--calc-line);
  }

  .tt-link {
    color: #1a5fd0;
    text-decoration: underline;
  }

  .tt-muted {
    color: var(--calc-muted);
  }

  .tt-footnote {
    margin: 8px 0 22px;
    font-size: var(--fs-2xs);
    color: var(--calc-muted);
  }

  /* ── collapsible groups ── */
  .tt-acc {
    border: 1px solid var(--calc-line);
    border-radius: 8px;
    margin-bottom: 10px;
    overflow: hidden;
  }

  .tt-acc__head {
    appearance: none;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    background: #fff;
    border: 0;
    cursor: pointer;
    text-align: left;
  }

  .tt-acc__title {
    font-size: var(--fs-sm);
    font-weight: 700;
    color: var(--calc-navy);
  }

  .tt-acc__summary {
    margin-left: auto;
    font-size: var(--fs-sm);
    font-weight: 700;
    color: var(--calc-ink);
  }

  .tt-acc__chev {
    color: var(--calc-muted);
    transition: transform 0.15s ease;
  }

  .tt-acc.is-open .tt-acc__chev {
    transform: rotate(180deg);
  }

  .tt-acc__body {
    border-top: 1px solid var(--calc-line);
  }

  .tt-note {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 14px 0 0;
    font-size: var(--fs-2xs);
    color: var(--calc-muted);
  }

  .tt-note i {
    font-size: 15px;
    color: var(--calc-ink);
  }

  .tt-state {
    padding: 28px 0;
    font-size: var(--fs-sm);
    color: var(--calc-muted);
  }

  .tt-state--error {
    color: #b42318;
  }

  /* ── narrow screens ── */
  @media (max-width: 820px) {
    .tt-card {
      padding: 0 14px 16px;
    }

    .tt-stats {
      grid-template-columns: 1fr;
      gap: 14px;
    }

    .tt-stat {
      padding: 0;
    }

    .tt-stat + .tt-stat {
      border-left: 0;
      border-top: 1px solid var(--calc-line);
      padding-top: 14px;
    }

    .tt-updated {
      margin-left: 0;
      width: 100%;
    }

    .tt-table-wrap {
      overflow-x: auto;
    }
  }
</style>
